import { getApiClient } from '~/utils/api'
import { normalizeBadgeLocale } from './contracts.mjs'
import type { BadgeCatalog, BadgeDefinition } from './types'
import type { MaybeRefOrGetter } from 'vue'

/** Shared in-flight reads only. The server owns its 30s TTL; refresh/re-entry always reads again. */
export function useBadgeCatalog(enabled: MaybeRefOrGetter<boolean> = true) {
  const { locale } = useI18n()
  const language = computed(() => normalizeBadgeLocale(locale.value))
  const active = computed(() => toValue(enabled))
  const apiBase = useRuntimeConfig().public.apiBaseUrl || 'https://server.musicmint.ai'
  const state = useState('mm-badge-catalog', () => ({ language: '', requestedLanguage: '', definitions: [] as BadgeDefinition[], loading: false, error: false, revision: 0 }))
  const definitions = computed(() => state.value.language === language.value && !state.value.error ? state.value.definitions : [])
  async function load(force = false) {
    const lang = language.value
    if (!active.value || !force && state.value.loading && state.value.requestedLanguage === lang) return
    const revision = ++state.value.revision
    if (state.value.language !== lang) state.value.definitions = []
    state.value.requestedLanguage = lang; state.value.loading = true; state.value.error = false
    try {
      const response = await getApiClient().post('music/v1/badges/definitions', { lang }, { musicmintInlineError: true, validateStatus: () => true })
      if (revision !== state.value.revision || language.value !== lang) return
      const data = response.data?.data as BadgeCatalog
      if (response.status < 200 || response.status >= 300 || response.data?.code !== 200 || !Array.isArray(data?.definitions) || data.definitions.some(item => !item || typeof item.key !== 'string' || !Array.isArray(item.thresholds))) throw new Error('Badge catalog unavailable')
      state.value.definitions = data.definitions; state.value.language = lang
    } catch {
      if (revision === state.value.revision && language.value === lang) state.value.error = true
    } finally {
      if (revision === state.value.revision) state.value.loading = false
    }
  }
  watch([active, language], () => { if (import.meta.client && active.value) void load() }, { immediate: import.meta.client, flush: 'sync' })
  if (getCurrentInstance()) onActivated(() => { if (active.value) void load() })
  return { definitions, state, load, apiBase }
}
